import React, { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

interface ModalProps {
    open: boolean;
    onClose: () => void;
    title: string;
    subtitle?: string;
    size?: "sm" | "md" | "lg" | "xl";
    children: React.ReactNode;
    footer?: React.ReactNode;
    /** Prevent closing on backdrop/Escape (e.g. while saving) */
    locked?: boolean;
}

const widths = {
    sm: "max-w-md",
    md: "max-w-xl",
    lg: "max-w-3xl",
    xl: "max-w-5xl",
};

export default function Modal({ open, onClose, title, subtitle, size = "md", children, footer, locked }: ModalProps) {
    useEffect(() => {
        if (!open) return;

        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !locked) onClose();
        };

        document.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, onClose, locked]);

    return (
        <AnimatePresence>
            {open && (
                <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => !locked && onClose()}
                        className="absolute inset-0 bg-[#020617]/80 backdrop-blur-sm"
                    />

                    <motion.div
                        role="dialog"
                        aria-modal="true"
                        initial={{ opacity: 0, y: 30, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 30, scale: 0.98 }}
                        transition={{ type: "spring", damping: 28, stiffness: 320 }}
                        className={`relative w-full ${widths[size]} max-h-[92vh] flex flex-col bg-slate-900/95 backdrop-blur-2xl border border-slate-800 rounded-t-3xl sm:rounded-3xl shadow-2xl`}
                    >
                        <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-slate-800">
                            <div>
                                <h2 className="text-lg font-bold text-white tracking-tight">{title}</h2>
                                {subtitle && <p className="text-sm text-slate-400 mt-0.5">{subtitle}</p>}
                            </div>
                            <button
                                onClick={onClose}
                                disabled={locked}
                                className="p-2 -m-2 rounded-xl text-slate-500 hover:text-white hover:bg-slate-800 transition-colors disabled:opacity-40"
                                aria-label="Close"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="px-6 py-5 overflow-y-auto">{children}</div>

                        {footer && (
                            <div className="px-6 py-4 border-t border-slate-800 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                                {footer}
                            </div>
                        )}
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}